/**
 * Links to an organization's private stored files (IOM FSA), in chat text the
 * workspace mirrored from the legacy app.
 *
 * Such a file is an `amoreg-file:` reference, not a URL
 * (`@repo/shared/organizationFileStorage`). The browser reads it from the
 * server's own file route, which checks the session's membership of the
 * organization; nothing public is involved. A link only works with the site
 * cookie, so a session authenticated otherwise (the desktop shell, a
 * cross-site backend) gets no link rather than a request that can only fail.
 */
import {
  isOrganizationFileReference,
  ORGANIZATION_FILES_PATH,
  parseOrganizationFileReference,
  formatOrganizationFileReference,
} from "@repo/shared/organizationFileStorage";

import { primaryHttpUsesBrowserSession } from "~/environments/primary/httpLayer";
import { resolvePrimaryEnvironmentHttpUrl } from "~/environments/primary/target";

/**
 * The server URL for a private file reference; `""` (no link) for a malformed
 * one or a session without the site cookie; `null` when `href` is not a
 * reference at all, so the caller's other link handling applies.
 */
export function organizationFileHref(href: string): string | null {
  if (!isOrganizationFileReference(href)) return null;
  const reference = parseOrganizationFileReference(href);
  if (reference === null) return "";
  try {
    if (!primaryHttpUsesBrowserSession()) return "";
    return resolvePrimaryEnvironmentHttpUrl(ORGANIZATION_FILES_PATH, {
      organizationId: reference.organizationId,
      reference: formatOrganizationFileReference(reference),
    });
  } catch {
    return "";
  }
}

/**
 * An org mark as the server answers it: a private one is a server-relative
 * file route (`orgsRead`), which must be resolved against the server's own
 * origin, not the page's; `null` when this session cannot load it with the
 * site cookie. Any other URL is unchanged.
 */
export function organizationMarkUrl(url: string | null): string | null {
  if (url === null || !url.startsWith(`${ORGANIZATION_FILES_PATH}?`)) return url;
  const query = new URLSearchParams(url.slice(ORGANIZATION_FILES_PATH.length + 1));
  const reference = query.get("reference");
  return reference === null ? null : organizationFileHref(reference) || null;
}
